import { ArrowDown, ArrowRight, GitBranch, Mail, Microscope, Cpu } from "lucide-react";
import useReveal from "../../components/useReveal";
import { Ic, icons } from "../../components/Icons";
import { CAREERS_EMAIL, CAREERS_PERKS, CAREER_OPPORTUNITIES } from "./careersData";

const reasonsToJoin = [
  {
    icon: Cpu,
    title: "Deep technical problems",
    text: "Work across computer architecture, FPGA and ASIC development, 3D graphics, spatial AI and real-time systems.",
  },
  {
    icon: GitBranch,
    title: "Meaningful ownership",
    text: "Join early, influence technical decisions and take responsibility for important parts of the product.",
  },
  {
    icon: Microscope,
    title: "Research into reality",
    text: "Turn ideas from graphics, hardware and machine learning research into systems that can be measured, tested and deployed.",
  },
];

const workPrinciples = [
  "Ownership over supervision",
  "Clear communication",
  "High standards, low ego",
];

function CareerRoleCard({ role }) {
  return (
    <article className="careers-role card-dark">
      <div className="careers-role-inner">
        <div className="careers-role-body">
          <div className="careers-role-meta">
            <span className="careers-role-pill careers-role-pill--team">{role.team}</span>
            <span className="careers-role-pill careers-role-pill--muted">{role.location}</span>
          </div>
          <h3 className="careers-role-title">{role.title}</h3>
          <p className="careers-role-desc">{role.description}</p>
        </div>
        <div className="careers-role-apply">
          <a className="careers-role-apply-link" href={role.href || CAREERS_EMAIL}>
            Apply
            <span className="careers-role-apply-arrow" aria-hidden="true">
              <ArrowRight size={15} strokeWidth={1.9} />
            </span>
          </a>
        </div>
      </div>
    </article>
  );
}

export default function CareersPage() {
  useReveal();

  return (
    <div className="careers-page">
      <section className="careers-hero section section-dark" aria-labelledby="careers-title">
        <div className="container-narrow careers-hero-inner">
          <div className="reveal eyebrow">Careers at Ateno</div>
          <h1 id="careers-title" className="reveal reveal-d1 headline careers-hero-title">
            Build the spatial layer of the world.
          </h1>
          <p className="reveal reveal-d2 careers-hero-lead">
            Join Ateno at an early stage and help build the hardware and software infrastructure behind spatial computing, 3D vision and real-time spatial intelligence.
          </p>
          <p className="reveal reveal-d3 careers-hero-supporting">
            We are assembling a small, highly technical team of engineers and researchers who want meaningful ownership, difficult problems and the opportunity to shape a new computing platform from the beginning.
          </p>
          <div className="reveal reveal-d4 careers-hero-actions">
            <a href="#opportunities" className="btn btn-white btn-lg">
              Explore opportunities
              <ArrowDown size={17} strokeWidth={1.9} aria-hidden="true" />
            </a>
            <a href={CAREERS_EMAIL} className="btn btn-ghost btn-lg">
              <Mail size={17} strokeWidth={1.9} aria-hidden="true" />
              Introduce yourself
            </a>
          </div>
        </div>
      </section>

      <section className="section section-darker" aria-labelledby="why-join-title">
        <div className="container">
          <div className="careers-section-heading">
            <div className="reveal eyebrow">Why join Ateno</div>
            <h2 id="why-join-title" className="reveal headline careers-section-title">
              Build at the edge of what is possible.
            </h2>
          </div>
          <div className="careers-reasons-grid">
            {reasonsToJoin.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <article key={reason.title} className={`reveal reveal-d${index + 1} card-dark careers-reason-card`}>
                  <div className="feat-icon-wrap" aria-hidden="true">
                    <Icon size={20} strokeWidth={1.7} />
                  </div>
                  <h3>{reason.title}</h3>
                  <p>{reason.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section section-dark" aria-labelledby="how-we-work-title">
        <div className="container careers-work-grid">
          <div>
            <div className="reveal eyebrow">How we work</div>
            <h2 id="how-we-work-title" className="reveal headline careers-section-title">
              Flexible, focused and collaborative.
            </h2>
          </div>
          <div className="careers-work-copy">
            <p className="reveal subhead">
              Ateno operates as a flexible hybrid team. Most work can be completed remotely, with regular virtual collaboration and occasional in-person sessions for prototyping, product development, research and strategic planning. Team members are trusted to manage their schedules and work from the environment where they perform best.
            </p>
            <ul className="reveal careers-principles" aria-label="How we work principles">
              {workPrinciples.map((principle) => (
                <li key={principle}>
                  <Ic d={icons.check} size={12} color="currentColor" sw={2.5} />
                  <span>{principle}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {CAREERS_PERKS.length > 0 && (
      <section className="section section-darker" aria-labelledby="benefits-title">
        <div className="container">
          <div className="careers-section-heading">
            <div className="reveal eyebrow">Benefits and support</div>
            <h2 id="benefits-title" className="reveal headline careers-section-title">
              Benefits
            </h2>
          </div>
          <p className="reveal subhead">Equity and flexible hybrid work.</p>
          <div className="careers-perks-grid">
            {CAREERS_PERKS.map((perk, index) => (
              <div key={perk} className={`reveal reveal-d${(index % 3) + 1} careers-perk`}>
                <span className="careers-perk-check" aria-hidden="true">
                  <Ic d={icons.check} size={13} color="currentColor" sw={2.5} />
                </span>
                <span className="careers-perk-text">{perk}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      )}

      <section id="opportunities" className="section section-dark" aria-labelledby="opportunities-title">
        <div className="container">
          <div className="careers-section-heading careers-opportunities-heading">
            <div className="reveal eyebrow">Current opportunities</div>
            <h2 id="opportunities-title" className="reveal headline careers-section-title">
              We are building our first technical team.
            </h2>
          </div>

          {CAREER_OPPORTUNITIES.length > 0 ? (
            <div className="careers-roles">
              {CAREER_OPPORTUNITIES.map((role, index) => (
                <div key={role.title} className={`reveal reveal-d${(index % 3) + 1}`}>
                  <CareerRoleCard role={role} />
                </div>
              ))}
            </div>
          ) : (
            <div className="reveal careers-open-application card-dark">
              <div className="careers-open-application-copy">
                <p>
                  We are interested in hearing from exceptional engineers and researchers working in computer architecture, FPGA or ASIC development, computer graphics, 3D vision, machine learning systems and spatial computing.
                </p>
                <p className="careers-open-application-note">
                  Please include a short introduction, your CV or portfolio and links to relevant technical work.
                </p>
              </div>
              <a href={CAREERS_EMAIL} className="btn btn-white btn-md">
                <Mail size={16} strokeWidth={1.9} aria-hidden="true" />
                Introduce yourself
              </a>
            </div>
          )}
        </div>
      </section>

      <section className="section section-darker careers-closing" aria-labelledby="careers-closing-title">
        <div className="container-narrow">
          <div className="reveal eyebrow">Careers at Ateno</div>
          <h2 id="careers-closing-title" className="reveal headline careers-closing-title">
            Help shape the infrastructure behind the spatial era.
          </h2>
          <a href={CAREERS_EMAIL} className="reveal btn btn-white btn-lg">
            Start a conversation
            <ArrowRight size={17} strokeWidth={1.9} aria-hidden="true" />
          </a>
        </div>
      </section>
    </div>
  );
}
